import React, { useEffect } from 'react';
import { InformationTooltip } from '../Map/MapInfoTooltip/MapInfoTooltip';
import Popup from 'reactjs-popup';
import { Typography } from '@mui/joy';
import RunTestSingle from './RunTestSingle';
import RunTestBulk from './RunTestBulk';
import Logo from '../../assets/images/utility-icons/info.svg?react';
import BulkIcon from '../../assets/images/utility-icons/bulk.svg?react';
import HeaderPlusIcon from '../../assets/images/utility-icons/single.svg?react';
import runTestType from '../../utils/runTestType';
import './RunTest.css';
import { useHttpClient } from '../../hooks/http-hook';
import LoadingSpinner from '../UI/LoadingSpinner/LoadingSpinner';
import { useCheckTestRateLimitQuery } from '../../services/api.user';

const TESTS_RATE_LIMIT_WARNING_THRESHOLD = 900;
const TESTS_RATE_LIMIT_BLOCK_THRESHOLD = 1000;

interface RunTestProps {
  onClose: () => void;
  setShowSuccessInfo: React.Dispatch<React.SetStateAction<boolean>>;
  setTestType: React.Dispatch<React.SetStateAction<string>>;
}

export interface PageType {
  _id: string;
  label: string;
  value: string;
}

export interface Test {
  name: string;
  pageType: string;
  label: string;
}

export const RUN_TEST_URL = import.meta.env.VITE_BACKEND_URL + '/api/athena/testautomationrequest';

const tooltipText =
  'To run test please insert page URL and choose Page type. Add and delete URL entries by using icons by the right side of entry fields. Notification after test is done will come to your email address associated with your account. If you want to add any other person to notification please put additional email address to input at the bottom of the pop-up. Separate additional e-mail addresses with a comma, a semicolon or by pressing space or enter. Only @samsung.com and @cheil.com addresses are accepted.';

const { RUN_TEST, BULK_TEST } = runTestType;

const tabs = [
  {
    name: RUN_TEST,
    title: 'Run Test as single',
    icon: <HeaderPlusIcon />
  },
  {
    name: BULK_TEST,
    title: 'Run Test as bulk',
    icon: <BulkIcon />
  }
];

export const RunTest = ({ onClose, setShowSuccessInfo, setTestType }: RunTestProps) => {
  const [activeTab, setActiveTab] = React.useState(RUN_TEST);
  const [pageTypes, setPageTypes] = React.useState<PageType[]>([]);
  const { data: rateLimitResponse } = useCheckTestRateLimitQuery();
  const usedTokens = rateLimitResponse?.usedTokens || 0;

  const { sendRequest, isLoading } = useHttpClient();

  const changeActiveTab = (tabName: React.SetStateAction<string>) => {
    setActiveTab(tabName);
  };

  const handleCreate = (values: any, setValues: any) => {
    const tests = [...values.tests];

    tests.push({
      name: '',
      pageType: '',
      label: ''
    });

    setValues({
      ...values,
      tests
    });
  };

  const handleDelete = (values: any, setValues: any, index: number) => {
    const tests = [...values.tests];

    tests.splice(index, 1);

    setValues({
      ...values,
      tests
    });
  };

  const handleRenderContent = () => {
    switch (activeTab) {
      case RUN_TEST:
        return (
          <RunTestSingle
            onClose={onClose}
            pageTypes={pageTypes}
            setShowSuccessInfo={setShowSuccessInfo}
            handleCreate={handleCreate}
            handleDelete={handleDelete}
            setTestType={setTestType}
          />
        );
      case BULK_TEST:
        return (
          <RunTestBulk
            onClose={onClose}
            pageTypes={pageTypes}
            setShowSuccessInfo={setShowSuccessInfo}
            handleCreate={handleCreate}
            handleDelete={handleDelete}
            setTestType={setTestType}
          />
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    const fetchPageTypes = async () => {
      await sendRequest(`${import.meta.env.VITE_BACKEND_URL}/api/users/pagetypes`, 'GET', null, {
        'content-type': 'application/json'
      }).then((res) => {
        setPageTypes(res);
      });
    };

    fetchPageTypes();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className="center">
        <LoadingSpinner />
      </div>
    );
  }

  const showRateLimitWarning = usedTokens >= TESTS_RATE_LIMIT_WARNING_THRESHOLD;
  return (
    <div className="run-test-container">
      <div className="run-test-header">
        {tabs.map((tab) => (
          <div
            key={tab.name}
            className={`run-test-header-btn ${activeTab === tab.name ? 'run-test-header-btn-active' : ''}`}
            onClick={() => changeActiveTab(tab.name)}
          >
            {tab.icon}
            {tab.title}
          </div>
        ))}

        <Popup
          trigger={
            <div className="svg-button-info">
              <Logo />
            </div>
          }
          position={'bottom right'}
          on={['hover', 'focus']}
          arrow
        >
          <InformationTooltip title={'Run test'} text={tooltipText} optionalText={undefined} />
        </Popup>
      </div>
      <hr />
      <div>{handleRenderContent()}</div>
      {showRateLimitWarning && (
        <Typography color="danger" fontWeight={600} textAlign="center" paddingBottom={4}>
          You have {TESTS_RATE_LIMIT_BLOCK_THRESHOLD - usedTokens} tokens left to run tests. If you exceed this limit,
          you will be blocked.
        </Typography>
      )}
    </div>
  );
};
